import { useState } from 'react';
import { beverages, beverageCategories } from '../../../mocks/beverages';
import { Link } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';
import { useToast } from '../../../contexts/ToastContext';

export default function MenuSection() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [addedItem, setAddedItem] = useState<string | null>(null);
  const { addItem } = useCart();
  const toast = useToast();

  const filtered = activeCategory === 'all'
    ? beverages
    : beverages.filter((b) => b.category === activeCategory);

  const handleAddToCart = (bev: typeof beverages[0], e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({ id: bev.id, name: bev.name, price: bev.price, image: bev.image, volume: bev.volume });
    setAddedItem(bev.id);
    setTimeout(() => setAddedItem(null), 1500);
    toast.cart(`${bev.name} added!`, `GYD $${bev.price.toLocaleString()} · ${bev.volume}`);
  };

  const formatPrice = (price: number) => `GYD $${price.toLocaleString()}`;

  return (
    <section id="menu" className="py-24 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <span className="inline-block bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-widest uppercase">
              Interactive Menu
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.02em' }}>
              Featured <span className="text-[#FF6B35]">/</span> Beverages
            </h2>
          </div>
          <p className="text-gray-500 text-sm max-w-xs leading-relaxed">
            Browse our curated selection of the finest beverages available for delivery in Georgetown. Filter by type to find exactly what you need.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          {beverageCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? 'bg-[#FF6B35] text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#FF6B35] hover:text-[#FF6B35]'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-product-shop>
          {filtered.map((bev) => (
            <Link
              key={bev.id}
              to={`/beverage/${bev.id}`}
              className="bg-white rounded-2xl overflow-hidden group hover:scale-[1.02] transition-transform duration-300 block"
            >
              {/* Image */}
              <div className="relative h-52 bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden">
                <img
                  src={bev.image}
                  alt={bev.name}
                  className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-110"
                />
                {bev.badge && (
                  <span className="absolute top-3 left-3 bg-[#FF6B35] text-white text-xs font-bold px-2 py-1 rounded-lg">
                    {bev.badge}
                  </span>
                )}
                {bev.originalPrice && (
                  <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-lg">
                    SALE
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center gap-1 mb-1">
                  <div className="w-4 h-4 flex items-center justify-center text-amber-400">
                    <i className="ri-star-fill text-xs" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700">{bev.rating}</span>
                  <span className="text-xs text-gray-400">({bev.reviews})</span>
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-0.5">{bev.name}</h3>
                <p className="text-gray-400 text-xs mb-3">{bev.volume}</p>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-black text-gray-900 text-sm">{formatPrice(bev.price)}</span>
                    {bev.originalPrice && (
                      <span className="text-gray-400 text-xs line-through ml-1">{formatPrice(bev.originalPrice)}</span>
                    )}
                  </div>
                  <button
                    onClick={(e) => handleAddToCart(bev, e)}
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-white transition-all cursor-pointer ${
                      addedItem === bev.id ? 'bg-green-500 scale-110' : 'bg-[#FF6B35] hover:bg-[#e85d2a]'
                    }`}
                  >
                    <i className={`text-sm ${addedItem === bev.id ? 'ri-check-line' : 'ri-add-line'}`} />
                  </button>
                </div>

                <div className="flex items-center gap-1 mt-2">
                  <div className="w-3 h-3 flex items-center justify-center text-[#FF6B35]">
                    <i className="ri-time-line text-xs" />
                  </div>
                  <span className="text-xs text-gray-400">{bev.deliveryTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* View All CTA */}
        <div className="flex justify-center mt-12">
          <Link
            to="/menu"
            className="bg-[#0D0D0D] text-white font-semibold px-10 py-4 rounded-full flex items-center gap-3 hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            View Full Menu
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
